import React, {Component} from 'react';
import {Col, ControlLabel, FormControl, FormGroup, Glyphicon, InputGroup} from 'react-bootstrap';
import Pikaday from 'pikaday';
import PropTypes from 'prop-types';
import moment from 'moment'
import {Field} from "redux-form";
import {ISO_DATE, ISO_DATE_TIME} from "../../utils/date";

export const formatDate = (v) => {
	if (!v) {
		return null
	}
	return new Date(v)
};


class ReactPikadayComponent extends React.Component {
	static propTypes = {
		id: PropTypes.string,
		type: PropTypes.string,
		value: PropTypes.instanceOf(Date),
		onChange: PropTypes.func,
		disabled: PropTypes.bool,
		placeholder: PropTypes.string,
		readOnly: PropTypes.bool,
		name: PropTypes.string,
		style: PropTypes.object,
		tabIndex: PropTypes.number,
		valueLink: PropTypes.shape({
			value: PropTypes.instanceOf(Date),
			requestChange: PropTypes.func.isRequired
		})

		// see Pikaday options at https://github.com/dbushell/Pikaday#configuration
		// except `onSelect` and `field`
	};

	static defaultProps = {
		type: 'text'
	};

	componentDidMount() {
		const {value} = this._getValueLink(this.props);

		this._setupPikaday();
		this._setDateIfChanged(value);
	}

	componentWillReceiveProps(nextProps) {
		const newDate = this._getValueLink(nextProps).value;
		const lastDate = this._getValueLink(this.props).value;

		this._setDateIfChanged(newDate, lastDate);
		this._setMinDateIfChanged(nextProps.minDate, this.props.minDate);
		this._setMaxDateIfChanged(nextProps.maxDate, this.props.maxDate);
	}

	componentDidUpdate(prevProps) {
		// update if container is set
		if (!prevProps.container && this.props.container) {
			const newDate = this._getValueLink(this.props).value;
			const lastDate = this._getValueLink(prevProps).value;

			this.pikaday.destroy();
			this._setupPikaday();
			this._setDateIfChanged(newDate, lastDate);
		}
	}

	componentWillUnmount() {
		this.pikaday.destroy();
	}

	onBlur() {
		const v = this.refs.pikaday.value;
		if (!v) {
			this.props.onChange(null);
			return;
		}
		if (v && !moment(v, moment.localeData().longDateFormat('L')).isValid()) {
			this.props.onChange(null);
		}
	}

	render() {
		const {id, type, className, name, tabIndex, disabled, placeholder, readOnly, style} = this.props;

		return (
			<input
				id={id}
				type={type}
				ref="pikaday"
				name={name}
				className={className}
				style={style}
				placeholder={placeholder}
				disabled={disabled}
				readOnly={readOnly}
				tabIndex={tabIndex}
				onBlur={() => this.onBlur()}
			/>
		);
	}

	_getValueLink(props) {
		return props.valueLink || {
			value: props.value,
			requestChange: props.onChange
		};
	}

	_setupPikaday() {
		const el = this.refs.pikaday;
		const {requestChange} = this._getValueLink(this.props);
		const {value, onChange, valueLink, ...pikadayOptions} = this.props; // eslint-disable-line no-unused-vars
		const options = Object.assign({}, pikadayOptions, {
			field: el,
			onSelect: requestChange
		});

		this.pikaday = new Pikaday(options);
	}

	_setDateIfChanged(newDate, prevDate) {
		this._setDate(newDate, prevDate, newTime => {
			if (isNaN(newTime)) {
				// workaround for pikaday not clearing value when date set to false
				const el = this.refs.pikaday;
				el.value = '';
			}
			this.pikaday.setDate(newDate, true); // not trigger onSelect
		});
	}

	_setMinDateIfChanged(newDate, prevDate) {
		this._setDate(newDate, prevDate, () => {
			this.pikaday.setMinDate(newDate);
		});
	}

	_setMaxDateIfChanged(newDate, prevDate) {
		this._setDate(newDate, prevDate, () => {
			this.pikaday.setMaxDate(newDate);
		});
	}

	_setDate(newDate, prevDate, setter) {
		const newTime = newDate ? newDate.getTime() : null;
		const prevTime = prevDate ? prevDate.getTime() : null;

		if (newTime !== prevTime) {
			setter(newTime);
		}
	}
}

class DateField extends Component {

	constructor(props) {
		super(props);

		this.state = {
			date: null,
			time: null,
			timeValue: ""
		}

		this.handleDateChange = this.handleDateChange.bind(this);
		this.handleTimeChange = this.handleTimeChange.bind(this);
		this.handleOpenClick = this.handleOpenClick.bind(this);
	}

	componentWillMount() {
		this.syncState(this.props);
	}

	componentWillReceiveProps(nextProps) {
		this.syncState(nextProps);
	}

	syncState(props) {
		const {input: {value}} = props;
		const dt = value ? moment(value) : null;
		const s = {
			date: dt ? dt.format(ISO_DATE) : null,
			time: dt ? dt.format("HH:mm:ss") : null,
			timeValue: dt ? (dt.format("HH:mm") || "") : ""
		}
		this.setState(s);
	}

	handleDateChange(date) {
		this.setState({date: date ? moment(date).format(ISO_DATE) : null}, this.onValueChange);
		if (!this.state.timeValue && this.timeInput) {
			setTimeout(() => {
				this.timeInput.focus();
			}, 100);
		}
	}

	handleTimeChange(e) {
		let v = (e.target || e).value;
		if (v && v.indexOf(":") < 0 && v.length > 2) {
			v = v.substring(0, 2) + ":" + (v.substring(2) + "00").substring(0, 2);
		}
		this.setState({time: v ? v + ":00" : null}, this.onValueChange);
	}

	onValueChange() {
		const {date, time, timeValue} = this.state;
		const {onChangeHook, input, withTime} = this.props;
		let value = null;
		if (date && (!withTime || time)) {
			const parsed = withTime
				? moment(date + " " + time, ISO_DATE_TIME)
				: moment(date, ISO_DATE);
			if (!isNaN(parsed) && parsed.isValid() && (!withTime || time.match(/\d{1,2}(:\d{1,2})?/))) {
				value = parsed.format(withTime ? ISO_DATE_TIME : ISO_DATE);
				if (withTime) {
					const t = moment(value).format("HH:mm");
					if (t !== timeValue) {
						this.setState({timeValue: t})
					}
				}
			}
		}

		if (value || input.value) {
			if (onChangeHook) {
				onChangeHook(value)
			}
			input.onChange(value);
		}
	}

	handleOpenClick() {
		this.refs.pika.pikaday.show()
	}

	render() {

		const {
			feedbackIcon,
			input,
			label,
			title,
			sizes,
			clearable,
			withTime,
			onChangeHook,
			meta: {error, warning, touched},
			...props
		} = this.props;
		const {date, timeValue} = this.state;

		let message;
		const validationState = touched ? (error && "error") || (warning && "warning") || null : null;

		if (touched && (error || warning)) {
			message = <span className="help-block">{error || warning}</span>;
		}

		return (
			<FormGroup controlId={input.name} validationState={validationState} className={'date-control'} title={title}>
				<Col sm={sizes && sizes[0] ? sizes[0] : 4} componentClass={ControlLabel}>{label}</Col>
				<Col sm={sizes && sizes[1] ? sizes[1] : 8}>
					<InputGroup className={'date-picker' + (withTime ? ' time-picker' : '')}>
						<ReactPikadayComponent
							ref="pika"
							i18n={{
								months: moment.localeData()._months,
								weekdays: moment.localeData()._weekdays,
								weekdaysShort: moment.localeData()._weekdaysShort
							}}
							format={moment.localeData().longDateFormat('L')}
							className="form-control date-picker-date"
							maxLength={12}
							{...input}
							{...props}
							value={date ? new Date(date) : null}
							onChange={this.handleDateChange}
							type={"text"}
						/>
						{withTime && <FormControl className='date-picker-time'
							inputRef={(ref) => {
								this.timeInput = ref
							}}
							value={timeValue}
							onChange={(e) => {
								this.setState({timeValue: e.target.value})
							}}
							onBlur={this.handleTimeChange}
							placeholder={"HH:MM"}
							type={'text'}
							maxLength={5}
						/>}
						{(!clearable || !input.value) && <InputGroup.Addon className='date-picker-btn' onClick={this.handleOpenClick}>
							<Glyphicon glyph="calendar"/>
						</InputGroup.Addon>}
						{clearable && input.value && <InputGroup.Addon className='date-picker-btn' onClick={() => {
							input.onChange(null)
						}}>
							<Glyphicon glyph="trash"/>
						</InputGroup.Addon>}
					</InputGroup>
					{feedbackIcon ? <FormControl.Feedback>{feedbackIcon}</FormControl.Feedback> : null}
					{message}
				</Col>
			</FormGroup>
		);
	}
}

export default DateField;

export type DateFieldProps = {
	label: string,
	name: string,
	title?: string,
	minDate?: Date,
	maxDate?: Date,
	clearable: boolean,
	withTime: boolean,
	onChangeHook: (v: Date | string | number) => void,
	placeholder?: string
}

export const renderDateField = (props: DateFieldProps) => {
	const {minDate, maxDate, ...restOfTheProps} = props;
	return <Field {...restOfTheProps}
		type="text"
		minDate={minDate ? moment(minDate).toDate() : undefined}
		maxDate={maxDate ? moment(maxDate).toDate() : undefined}
		component={DateField}
	/>;
}



// WEBPACK FOOTER //
// ./src/components/form/DateField.js